import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getCountriesWithShippingCategories } from "../services/Public/PublicPagesApi";
import { resolveImageUrl } from "../services/indexMediaBaseUrl";

const STORAGE_COUNTRY_CODE = "selectedCountryCode"; // نفس Navbar
const STORAGE_COUNTRY_ID = "country_id";
const STORAGE_LANG = "lang";

export default function CountryLanguageGate() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem(STORAGE_LANG);
    if (stored === "rn") return "en";
    return stored?.startsWith("ar") ? "ar" : "en";
  });

  const [selectedCountryCode, setSelectedCountryCode] = useState(() => {
    return localStorage.getItem(STORAGE_COUNTRY_CODE) || "";
  });

  const isAr = lang === "ar";

  // افتح البوب اب لو ناقص دولة أو لغة
  useEffect(() => {
    const hasLang = !!localStorage.getItem(STORAGE_LANG);
    const hasCountry = !!localStorage.getItem(STORAGE_COUNTRY_ID);
    if (!hasLang || !hasCountry) setOpen(true);
  }, []);

  // جلب الدول بنفس دالتك
  useEffect(() => {
    let mounted = true;
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const res = await getCountriesWithShippingCategories();
        const list = res?.countries || res?.data?.countries || res?.data || [];
        if (mounted) setCountries(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("CountryLanguageGate:getCountries error", err);
      } finally {
        if (mounted) setLoadingCountries(false);
      }
    };
    fetchCountries();
    return () => {
      mounted = false;
    };
  }, []);

  const selectedCountry = useMemo(() => {
    return (
      (countries || []).find(
        (c) =>
          String(c.id) === String(selectedCountryCode) ||
          String(c.code) === String(selectedCountryCode)
      ) || null
    );
  }, [countries, selectedCountryCode]);

  const displayedCountryName = useMemo(() => {
    if (!selectedCountry) return "";
    return isAr ? selectedCountry.name_ar : selectedCountry.name_en;
  }, [selectedCountry, isAr]);

  // تطبيق اللغة + اتجاه الصفحة
  const applyLanguage = async (newLang) => {
    const normalized = newLang === "ar" ? "ar" : "en";
    setLang(normalized);
    try {
      await i18n.changeLanguage(normalized);
    } catch {}
    localStorage.setItem(STORAGE_LANG, normalized);
    document.documentElement.dir = normalized === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = normalized;
  };

  const handleCountryChange = (e) => {
    const code = e.target.value || "";
    setSelectedCountryCode(code);
  };

  // helper to render country flag image
  const CountryFlag = ({ country, size = 24 }) => {
    if (!country) return null;
    const imgSrc = country.image ? resolveImageUrl(country.image) : null;
    if (imgSrc) {
      return (
        <Box
          component="img"
          src={imgSrc}
          alt={country.name_en || country.name_ar || ""}
          sx={{
            width: size,
            height: Math.round(size * 0.7),
            objectFit: "cover",
            borderRadius: 0.5,
            border: "1px solid",
            borderColor: "grey.200",
          }}
        />
      );
    }
    // fallback to emoji flag if no image
    return <span>{country.flag || ""}</span>;
  };

  // حفظ الدولة بنفس منطق Navbar (تخزين code + country_id)
  const persistCountry = (code) => {
    if (!code) return false;

    // selectedCountryCode
    localStorage.setItem(STORAGE_COUNTRY_CODE, code);

    // country_id
    const country = (countries || []).find(
      (c) => String(c.code) === String(code) || String(c.id) === String(code)
    );

    if (country?.id != null) {
      localStorage.setItem(STORAGE_COUNTRY_ID, String(country.id));
      return true;
    }

    // fallback لو code رقمي
    if (!isNaN(Number(code))) {
      localStorage.setItem(STORAGE_COUNTRY_ID, String(code));
      return true;
    }

    localStorage.removeItem(STORAGE_COUNTRY_ID);
    return false;
  };

  const canConfirm = !!lang && !!selectedCountryCode;

  const handleConfirm = async () => {
    await applyLanguage(lang);

    const ok = persistCountry(selectedCountryCode);
    if (!ok) return;

    setOpen(false);

    // مهم: علشان الأسعار/الـ context تتحدث
    window.location.reload();
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={{ fontWeight: 800 }}>
        {isAr ? "اختيار اللغة والدولة" : "Choose Language & Country"}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            {isAr
              ? "اختار اللغة والدولة لتسهيل ترشيح لكم المنتجات"
              : "Choose your language and country to help us recommend products for you."}
          </Typography>

          {/* Language */}
          <FormControl fullWidth size="small">
            <InputLabel>{isAr ? "اللغة" : "Language"}</InputLabel>
            <Select
              label={isAr ? "اللغة" : "Language"}
              value={lang}
              onChange={(e) => applyLanguage(e.target.value)}
            >
              <MenuItem value="ar">العربية</MenuItem>
              <MenuItem value="en">English</MenuItem>
            </Select>
          </FormControl>

          {/* Country */}
          <FormControl fullWidth size="small" disabled={loadingCountries}>
            <InputLabel>{isAr ? "الدولة" : "Country"}</InputLabel>
            <Select
              label={isAr ? "الدولة" : "Country"}
              value={selectedCountryCode}
              onChange={handleCountryChange}
              renderValue={(val) => {
                if (!val) return isAr ? "اختر الدولة" : "Choose country";
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CountryFlag country={selectedCountry} size={24} />
                    <span>{displayedCountryName}</span>
                  </Box>
                );
              }}
            >
              <MenuItem value="">
                <em>{isAr ? "اختر الدولة" : "Choose country"}</em>
              </MenuItem>

              {(countries || []).map((c) => (
                <MenuItem key={c.id} value={c.code || c.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CountryFlag country={c} size={24} />
                    <span>{isAr ? c.name_ar : c.name_en}</span>
                    <Typography
                      component="span"
                      sx={{ ml: 1, color: "text.secondary", fontSize: 12 }}
                    >
                      {c.code}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>

            {loadingCountries && (
              <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="caption" color="text.secondary">
                  {isAr ? "جاري تحميل الدول..." : "Loading countries..."}
                </Typography>
              </Box>
            )}
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm || loadingCountries}
          sx={{ fontWeight: 800 }}
        >
          {isAr ? "تأكيد" : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
